import useDialog, { DialogType } from '@/hooks/useDialog';
import { Form, FormField } from '@/components/ui/form';
import { useForm, useWatch } from 'react-hook-form';
import * as cancelField from '@/src/pages/RecyclingRequests/components/cancelModalField';
import { zodResolver } from '@hookform/resolvers/zod';
import FormItem from '@/components/molecule/FormItem';
import React, { useEffect } from 'react';
import useAuthStore from '@/src/store/useAuthStore';
import ConfirmDialog from '@/components/organism/ConfirmDialog';
import { CustomersDto } from '@/src/models/CustomersDto';
import { PutRecyclingRequestRequestBody } from '@/src/apis/rest/recyclingRequests.dtos';
import { RecyclingRequestDto } from '@/src/models/RecyclingRequestDto';

interface RequestCancelModalProps {
  requestData: RecyclingRequestDto | undefined;
  customer: CustomersDto | undefined;
  submitCallback?: () => Promise<any> | void;
  isOpen: boolean;
  setOpen: (open: boolean) => void;
}

function RequestCancelModal({
  requestData,
  customer,
  isOpen = false,
  setOpen,
}: RequestCancelModalProps) {
  const auth = useAuthStore(state => state.auth);

  const {
    Dialog: StatusConfirmDialog,
    isOpen: isStatusConfirmDialogOpen,
    openDialog: openStatusConfirmDialog,
    closeDialog: closeStatusConfirmDialog,
  } = useDialog({
    type: DialogType.ALERT,
  });

  const {
    Dialog: ErrorDialog,
    isOpen: isErrorDialogOpen,
    closeDialog: closeErrorDialog,
  } = useDialog({
    type: DialogType.ALERT,
  });

  const cancelForm = useForm<PutRecyclingRequestRequestBody>({
    resolver: zodResolver(cancelField.formSchema()),
    defaultValues: cancelField.DEFAULT_VALUES,
  });

  const watchFields = useWatch({ control: cancelForm.control });

  useEffect(() => {
    if (customer && auth && isOpen) {
      cancelForm.setValue(cancelField.formFields.nick.name, customer.nick);
      cancelForm.setValue(cancelField.formFields.cancelReason.name, '');
      cancelForm.setValue(cancelField.formFields.administrator.name, auth.name);
    }
  }, [isOpen, auth, cancelForm, customer]);

  return (
    <>
      <ErrorDialog
        title="수거 취소 불가"
        open={isErrorDialogOpen}
        onOpenChange={closeErrorDialog}
        cancelButtonProps={{ className: 'hidden' }}
      >
        {requestData &&
          requestData.status === 'PROCESSING' &&
          '이미 수거가 진행 중인 건은 수거 취소가 불가능합니다.'}
      </ErrorDialog>
      <StatusConfirmDialog
        title="수거 취소"
        open={isStatusConfirmDialogOpen}
        onOpenChange={closeStatusConfirmDialog}
        // onOk={() => handleCancelSubmit(cancelForm.getValues())}
        // isLoading={putRequestCancel.isPending}
      >
        수거를 취소하시겠습니까?
        <br />
        사용자에게는 수거 취소 상태로 보여지게 됩니다.
      </StatusConfirmDialog>
      <ConfirmDialog
        title="수거 취소 신청"
        open={isOpen}
        onOpenChange={setOpen}
        onOk={() => openStatusConfirmDialog()}
        okButtonProps={{ disabled: watchFields.cancelReason === '' }}
        // isLoading={putRequestCancel.isPending}
      >
        <Form {...cancelForm}>
          <form className="flex flex-col gap-[24px]">
            <FormField
              control={cancelForm.control}
              name={cancelField.formFields.nick.name}
              render={({ field, fieldState: { error } }) => {
                return (
                  <FormItem
                    label={cancelField.formFields.nick.label}
                    labelSize="M"
                    name={cancelField.formFields.nick.name}
                    type="text"
                    control={cancelForm.control}
                    direction="vertical"
                    inputProps={{
                      type: 'text',
                      disabled: true,
                      ...field,
                    }}
                    errorMessage={error?.message}
                  />
                );
              }}
            />
            <FormField
              control={cancelForm.control}
              name={cancelField.formFields.cancelReason.name}
              render={({ field, fieldState: { error } }) => {
                return (
                  <FormItem
                    label={cancelField.formFields.cancelReason.label}
                    labelSize="M"
                    name={cancelField.formFields.cancelReason.name}
                    type="textArea"
                    control={cancelForm.control}
                    direction="vertical"
                    errorMessage={error?.message}
                    rules={{
                      required: true,
                    }}
                    inputProps={{
                      className: 'h-[140px]',
                      placeholder:
                        cancelField.formFields.cancelReason.placeholder,
                      value: field.value,
                      onChange: e => field.onChange(e),
                    }}
                  />
                );
              }}
            />

            <FormField
              control={cancelForm.control}
              name={cancelField.formFields.administrator.name}
              render={({ field, fieldState: { error } }) => {
                return (
                  <FormItem
                    label={cancelField.formFields.administrator.label}
                    labelSize="M"
                    name={cancelField.formFields.administrator.name}
                    type="text"
                    control={cancelForm.control}
                    direction="vertical"
                    inputProps={{
                      type: 'text',
                      disabled: true,
                      ...field,
                    }}
                    errorMessage={error?.message}
                  />
                );
              }}
            />
          </form>
        </Form>
      </ConfirmDialog>
    </>
  );
}

export default React.memo(RequestCancelModal);
